import { useEffect, useState, type FormEvent } from 'react';
import { Bot, Download, Mail, Save, Send, Settings2 } from 'lucide-react';
import { api, ApiError } from '../api';
import { useAuth } from '../context/AuthContext';
import { useUi } from '../context/UiContext';
import type { AdminSettings, AiModerationPendingItem, AiModerationSettings, AiModerationTestResult } from '../types';
import { centsToInput, currencyInputToCents, formatTime } from '../utils';
import { Badge, Button, CollapsiblePanel, EmptyBlock, Field, LoadingBlock, PageHeader, Panel, Select, Textarea, TextInput } from '../components/ui';

type FeedbackProvider = 'qq' | '163' | '126' | 'gmail' | 'outlook' | 'custom';

const feedbackProviderOptions: Array<{ value: FeedbackProvider; label: string }> = [
  { value: 'qq', label: 'QQ 邮箱' },
  { value: '163', label: '163 邮箱' },
  { value: '126', label: '126 邮箱' },
  { value: 'gmail', label: 'Gmail' },
  { value: 'outlook', label: 'Outlook / Microsoft 365' },
  { value: 'custom', label: '自定义 SMTP' }
];

const feedbackProviderPresets: Record<Exclude<FeedbackProvider, 'custom'>, { host: string; port: number; secure: boolean }> = {
  qq: { host: 'smtp.qq.com', port: 465, secure: true },
  '163': { host: 'smtp.163.com', port: 465, secure: true },
  '126': { host: 'smtp.126.com', port: 465, secure: true },
  gmail: { host: 'smtp.gmail.com', port: 587, secure: false },
  outlook: { host: 'smtp.office365.com', port: 587, secure: false }
};

function detectFeedbackProvider(host: string): FeedbackProvider {
  const value = host.trim().toLowerCase();
  if (!value) return 'qq';
  if (value === 'smtp.qq.com') return 'qq';
  if (value === 'smtp.163.com') return '163';
  if (value === 'smtp.126.com') return '126';
  if (value === 'smtp.gmail.com') return 'gmail';
  if (value === 'smtp.office365.com' || value === 'smtp-mail.outlook.com') return 'outlook';
  return 'custom';
}

export function SettingsPage() {
  const { settings, permissions, refresh } = useAuth();
  const { toast, confirm } = useUi();
  const [form, setForm] = useState(settings);
  const [busy, setBusy] = useState(false);
  const [inviteCode, setInviteCode] = useState('');
  const [inviteMeta, setInviteMeta] = useState<AdminSettings | null>(null);
  const [feeRecipientUserId, setFeeRecipientUserId] = useState('');
  const [aiSettings, setAiSettings] = useState<AiModerationSettings | null>(null);
  const [aiKey, setAiKey] = useState('');
  const [aiClearKey, setAiClearKey] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [aiPending, setAiPending] = useState<AiModerationPendingItem[]>([]);
  const [aiTestResult, setAiTestResult] = useState<AiModerationTestResult | null>(null);
  const [feedbackPassword, setFeedbackPassword] = useState('');
  const [feedbackClearPassword, setFeedbackClearPassword] = useState(false);
  const [feedbackBusy, setFeedbackBusy] = useState(false);
  const [feedbackProvider, setFeedbackProvider] = useState<FeedbackProvider>('qq');

  useEffect(() => setForm(settings), [settings]);
  useEffect(() => { if (inviteMeta?.tradeFeeRecipientUserId) setFeeRecipientUserId(inviteMeta.tradeFeeRecipientUserId); }, [inviteMeta?.tradeFeeRecipientUserId]);
  useEffect(() => { if (inviteMeta) setFeedbackProvider(detectFeedbackProvider(inviteMeta.feedbackSmtpHost)); }, [inviteMeta?.feedbackSmtpHost]);
  const updateFeedback = <K extends keyof AdminSettings>(key: K, value: AdminSettings[K]) => {
    setInviteMeta(current => current ? { ...current, [key]: value } : current);
  };

  const saveFeedbackSettings = async (event: FormEvent) => {
    event.preventDefault();
    if (!inviteMeta) return;
    setFeedbackBusy(true);
    try {
      await api('/api/settings', {
        method: 'PATCH',
        body: JSON.stringify({
          feedbackEnabled: inviteMeta.feedbackEnabled,
          feedbackRecipientEmail: inviteMeta.feedbackRecipientEmail,
          feedbackSmtpHost: feedbackProvider === 'custom' ? inviteMeta.feedbackSmtpHost : feedbackProviderPresets[feedbackProvider].host,
          feedbackSmtpPort: feedbackProvider === 'custom' ? (Number(inviteMeta.feedbackSmtpPort) || 465) : feedbackProviderPresets[feedbackProvider].port,
          feedbackSmtpSecure: feedbackProvider === 'custom' ? inviteMeta.feedbackSmtpSecure : feedbackProviderPresets[feedbackProvider].secure,
          feedbackSmtpUser: inviteMeta.feedbackSmtpUser,
          feedbackFromEmail: feedbackProvider === 'custom' ? inviteMeta.feedbackFromEmail : '',
          feedbackSmtpPassword: feedbackPassword || undefined,
          clearFeedbackSmtpPassword: feedbackClearPassword
        })
      });
      setFeedbackPassword('');
      setFeedbackClearPassword(false);
      const latest = await api<{ settings: AdminSettings }>('/api/admin/settings');
      setInviteMeta(latest.settings);
      await refresh();
      toast('邮件反馈设置已保存', 'success');
    } catch (error) {
      toast(error instanceof ApiError ? error.message : '邮件反馈设置保存失败', 'error');
    } finally {
      setFeedbackBusy(false);
    }
  };

  const testFeedbackMail = async () => {
    setFeedbackBusy(true);
    try {
      const result = await api<{ recipient: string }>('/api/admin/feedback/test', { method: 'POST', body: '{}' });
      toast(`测试邮件已发送到 ${result.recipient}`, 'success');
    } catch (error) {
      toast(error instanceof ApiError ? error.message : '测试邮件发送失败', 'error');
    } finally {
      setFeedbackBusy(false);
    }
  };

  const loadAiModeration = async () => {
    if (!permissions.isSuperadmin) return;
    const [settingsData, pendingData] = await Promise.all([
      api<{ settings: AiModerationSettings }>('/api/admin/ai-moderation/settings'),
      api<{ items: AiModerationPendingItem[] }>('/api/admin/ai-moderation/pending')
    ]);
    setAiSettings(settingsData.settings);
    setAiPending(pendingData.items);
  };

  useEffect(() => {
    if (!permissions.canEditSettings) return;
    void api<{ settings: AdminSettings }>('/api/admin/settings').then(data => setInviteMeta(data.settings)).catch(() => setInviteMeta(null));
  }, [permissions.canEditSettings]);

  useEffect(() => {
    if (!permissions.isSuperadmin) return;
    void loadAiModeration().catch(error => toast(error instanceof ApiError ? error.message : 'AI审核设置加载失败', 'error'));
  }, [permissions.isSuperadmin]);

  if (!permissions.canEditSettings) return <EmptyBlock title="无权访问" description="系统设置仅对管理员开放。" />;

  const update = <K extends keyof typeof form>(key: K, value: (typeof form)[K]) => setForm(current => ({ ...current, [key]: value }));
  const updateAi = <K extends keyof AiModerationSettings>(key: K, value: AiModerationSettings[K]) => setAiSettings(current => current ? { ...current, [key]: value } : current);

  const aiPayload = () => aiSettings ? ({
    enabled: aiSettings.enabled,
    prompt: aiSettings.prompt,
    apiKey: aiKey || undefined,
    clearApiKey: aiClearKey
  }) : null;

  const persistAiSettings = async () => {
    const payload = aiPayload();
    if (!payload) throw new Error('AI审核设置尚未加载');
    await api('/api/admin/ai-moderation/settings', { method: 'PATCH', body: JSON.stringify(payload) });
    const latest = await api<{ settings: AiModerationSettings }>('/api/admin/ai-moderation/settings');
    setAiSettings(latest.settings);
    setAiKey('');
    setAiClearKey(false);
    return latest.settings;
  };

  const saveAiSettings = async (event: FormEvent) => {
    event.preventDefault();
    setAiBusy(true);
    try {
      await persistAiSettings();
      const pendingData = await api<{ items: AiModerationPendingItem[] }>('/api/admin/ai-moderation/pending');
      setAiPending(pendingData.items);
      toast('AI审核设置已保存', 'success');
    } catch (error) {
      toast(error instanceof ApiError ? error.message : 'AI审核设置保存失败', 'error');
    } finally {
      setAiBusy(false);
    }
  };

  const testAiSettings = async () => {
    setAiBusy(true);
    try {
      await persistAiSettings();
      const data = await api<{ result: AiModerationTestResult }>('/api/admin/ai-moderation/test', { method: 'POST', body: JSON.stringify({}) });
      setAiTestResult(data.result);
      const label = data.result.decision === 'allow' ? '允许' : data.result.decision === 'reject' ? '拒绝' : '建议人工复核';
      toast(`API 测试完成：${label}，风险分 ${data.result.riskScore}`, data.result.decision === 'allow' ? 'success' : 'info');
    } catch (error) {
      toast(error instanceof ApiError ? error.message : 'API 测试失败', 'error');
    } finally {
      setAiBusy(false);
    }
  };

  const reviewAiItem = async (item: AiModerationPendingItem, action: 'approve' | 'reject') => {
    try {
      await api(`/api/admin/ai-moderation/pending/${item.kind}/${item.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ action })
      });
      setAiPending(current => current.filter(entry => !(entry.kind === item.kind && entry.id === item.id)));
      toast(action === 'approve' ? '内容已通过并公开' : '内容已拒绝', 'success');
    } catch (error) {
      toast(error instanceof ApiError ? error.message : '审核操作失败', 'error');
    }
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    try {
      await api('/api/settings', {
        method: 'PATCH',
        body: JSON.stringify({
          siteName: form.siteName,
          currency: form.currency,
          initBalanceCents: currencyInputToCents(String(form.initBalanceCents / 100)),
          registrationEnabled: form.registrationEnabled,
          registrationInviteRequired: form.registrationInviteRequired,
          registrationInviteCode: inviteCode || undefined,
          registrationNotice: form.registrationNotice,
          commissionPolicy: form.commissionPolicy,
          marketPolicy: form.marketPolicy,
          auctionPolicy: form.auctionPolicy,
          auctionRequireReview: form.auctionRequireReview,
          tradeFeePercent: Number(form.tradeFeePercent) || 0,
          tradeFeeRecipientUserId: feeRecipientUserId || null,
          allowUnboundBoardMessages: form.allowUnboundBoardMessages,
          allowUnboundBoardComments: form.allowUnboundBoardComments,
          allowUnboundBoardLikes: form.allowUnboundBoardLikes
        })
      });
      setInviteCode('');
      const latest = await api<{ settings: AdminSettings }>('/api/admin/settings');
      setInviteMeta(latest.settings);
      await refresh();
      toast('系统设置已保存', 'success');
    } catch (error) {
      toast(error instanceof ApiError ? error.message : '保存失败', 'error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <PageHeader title="系统设置" description="管理站点名称、货币、注册开关和业务说明。" />
      <div className="two-column wide-left">
        <Panel title="基础设置" eyebrow="SYSTEM">
          <form className="stack-form" onSubmit={submit}>
            <div className="form-grid">
              <Field label="站点名称"><TextInput value={form.siteName} onChange={event => update('siteName', event.target.value)} maxLength={40} required /></Field>
              <Field label="虚拟货币名称"><TextInput value={form.currency} onChange={event => update('currency', event.target.value)} maxLength={12} required /></Field>
            </div>
            <Field label="物品交易手续费（%）" hint="市场成交和拍卖成交时从卖家收入中扣除，归入最早创建的活跃超级管理员账号；可随时修改。"><TextInput type="number" min="0" max="100" step="0.1" value={form.tradeFeePercent} onChange={event => update('tradeFeePercent', Number(event.target.value))} required /></Field>
            <Field label="手续费接收账号" hint="只能选择当前活跃的超级管理员"><Select value={feeRecipientUserId} onChange={event => setFeeRecipientUserId(event.target.value)}>{inviteMeta?.superadmins.map(account => <option key={account.id} value={account.id}>{account.displayName}（@{account.username}）</option>)}</Select></Field>
            <Field label="新成员初始资金"><TextInput type="number" min="0" step="0.01" value={centsToInput(form.initBalanceCents)} onChange={event => update('initBalanceCents', currencyInputToCents(event.target.value))} /></Field>
            <label className="check-line"><input type="checkbox" checked={form.registrationEnabled} onChange={event => update('registrationEnabled', event.target.checked)} /> 允许新成员自助注册</label>
            <label className="check-line"><input type="checkbox" checked={form.registrationInviteRequired} onChange={event => update('registrationInviteRequired', event.target.checked)} disabled={!form.registrationEnabled} /> 注册时必须填写邀请码</label>
            <Field label="更换注册邀请码" hint="仅在需要轮换时填写；邀请码只保存哈希，保存后无法找回原文">
              <TextInput value={inviteCode} onChange={event => setInviteCode(event.target.value.toUpperCase())} minLength={8} maxLength={64} pattern="[A-Za-z0-9_-]+" placeholder="输入新的邀请码" autoComplete="off" />
            </Field>
            <p className="form-note">当前邀请码状态：{inviteMeta?.registrationInviteConfigured ? '已设置，前缀 ' + (inviteMeta.registrationInvitePrefix || '****') + '****' : '未设置'}</p>
            <Field label="注册说明"><Textarea value={form.registrationNotice} onChange={event => update('registrationNotice', event.target.value)} rows={2} maxLength={500} /></Field>
            <div className="form-note"><strong>留言板权限</strong><br />以下开关只允许“已登录但未绑定游戏角色”的账号操作；关闭时仍要求绑定游戏角色。游客未登录时始终不能留言、评论或点赞。</div>
            <label className="check-line"><input type="checkbox" checked={form.allowUnboundBoardMessages} onChange={event => update('allowUnboundBoardMessages', event.target.checked)} /> 允许未绑定账号发布留言</label>
            <label className="check-line"><input type="checkbox" checked={form.allowUnboundBoardComments} onChange={event => update('allowUnboundBoardComments', event.target.checked)} /> 允许未绑定账号评论留言</label>
            <label className="check-line"><input type="checkbox" checked={form.allowUnboundBoardLikes} onChange={event => update('allowUnboundBoardLikes', event.target.checked)} /> 允许未绑定账号点赞留言</label>
            <Field label="委托规则说明"><Textarea value={form.commissionPolicy} onChange={event => update('commissionPolicy', event.target.value)} rows={2} maxLength={500} /></Field>
            <Field label="市场规则说明"><Textarea value={form.marketPolicy} onChange={event => update('marketPolicy', event.target.value)} rows={2} maxLength={500} /></Field>
            <div className="form-note"><strong>发起拍卖审核</strong><br />开启后，每次发起拍卖都需要管理员单独审核；玩家出价不受影响。关闭后，绑定玩家可以直接发布拍卖。</div>
            <label className="check-line"><input type="checkbox" checked={form.auctionRequireReview} onChange={event => update('auctionRequireReview', event.target.checked)} /> 发起拍卖需审核</label>
            <Field label="拍卖规则说明"><Textarea value={form.auctionPolicy} onChange={event => update('auctionPolicy', event.target.value)} rows={2} maxLength={500} /></Field>
            <Button variant="primary" type="submit" loading={busy}><Save size={17} /> 保存设置</Button>
          </form>
        </Panel>
        <div className="side-stack">
          <Panel title="权限边界" eyebrow="SECURITY">
            <div className="policy-list">
              <p><Settings2 size={17} /><span><strong>成员</strong>只能委托、购买、拍卖和兑换。</span></p>
              <p><Settings2 size={17} /><span><strong>管理员</strong>可管理普通成员、公告、兑换码和系统设置。</span></p>
              <p><Settings2 size={17} /><span><strong>超级管理员</strong>可管理管理员、任命与撤销管理员。</span></p>
            </div>
          </Panel>
          {permissions.canExportBackup && (
            <Panel title="完整备份" eyebrow="BACKUP">
              <p className="muted">导出包含全部业务表和审计记录。文件含敏感账号数据，请妥善保管。</p>
              <Button variant="danger" className="wide" onClick={() => void (async () => {
                const approved = await confirm({ title: '确认导出完整备份？', body: '导出文件包含所有账号数据、业务数据和审计记录，请妥善保管。', danger: true, confirmText: '确认导出' });
                if (approved) window.location.href = '/api/admin/backup';
              })()}><Download size={17} /> 导出 JSON 备份</Button>
            </Panel>
          )}
        </div>
      </div>

      <CollapsiblePanel title="邮件反馈" eyebrow="FEEDBACK MAIL" className="top-gap" defaultOpen={false}>
        {!inviteMeta ? <LoadingBlock label="正在加载邮件反馈设置" /> : (
          <form className="stack-form" onSubmit={saveFeedbackSettings}>
            <div className="form-note"><Mail size={17} /><span>玩家提交反馈后，系统用你的 SMTP 邮箱把内容发送到指定收件箱。常用邮箱只需选择服务商并填写账号与授权码，服务器地址会自动设置。</span></div>
            <label className="check-line"><input type="checkbox" checked={inviteMeta.feedbackEnabled} onChange={event => updateFeedback('feedbackEnabled', event.target.checked)} /> 开启玩家邮件反馈</label>
            <div className="form-grid">
              <Field label="接收反馈的邮箱" hint="玩家的反馈会发送到这里"><TextInput type="email" value={inviteMeta.feedbackRecipientEmail} onChange={event => updateFeedback('feedbackRecipientEmail', event.target.value)} placeholder="admin@qq.com" maxLength={200} /></Field>
              <Field label="邮箱服务"><Select value={feedbackProvider} onChange={event => setFeedbackProvider(event.target.value as FeedbackProvider)}>{feedbackProviderOptions.map(item => <option key={item.value} value={item.value}>{item.label}</option>)}</Select></Field>
              <Field label="发件邮箱账号" hint="通常是完整邮箱地址，例如 123456@qq.com"><TextInput value={inviteMeta.feedbackSmtpUser} onChange={event => updateFeedback('feedbackSmtpUser', event.target.value)} placeholder="123456@qq.com" maxLength={200} autoComplete="username" /></Field>
              <Field label="邮箱授权码" hint={inviteMeta.feedbackSmtpPasswordConfigured ? '已配置；留空不会修改现有授权码' : '在邮箱设置的 SMTP 服务中生成，不是邮箱登录密码'}>
                <TextInput type="password" value={feedbackPassword} onChange={event => { setFeedbackPassword(event.target.value); setFeedbackClearPassword(false); }} placeholder={inviteMeta.feedbackSmtpPasswordConfigured ? '留空保持现有授权码' : '输入 SMTP 授权码'} maxLength={500} autoComplete="new-password" disabled={feedbackClearPassword} />
              </Field>
            </div>
            {inviteMeta.feedbackSmtpPasswordConfigured && <label className="check-line"><input type="checkbox" checked={feedbackClearPassword} onChange={event => setFeedbackClearPassword(event.target.checked)} /> 删除已保存的授权码</label>}
            {feedbackProvider === 'custom' ? (
              <details className="feedback-smtp-advanced">
                <summary>自定义 SMTP 高级设置</summary>
                <div className="form-grid top-gap">
                  <Field label="SMTP 服务器"><TextInput value={inviteMeta.feedbackSmtpHost} onChange={event => updateFeedback('feedbackSmtpHost', event.target.value)} placeholder="smtp.example.com" maxLength={200} /></Field>
                  <Field label="SMTP 端口"><TextInput type="number" min="1" max="65535" value={inviteMeta.feedbackSmtpPort} onChange={event => updateFeedback('feedbackSmtpPort', Number(event.target.value) || 0)} /></Field>
                  <Field label="发件邮箱（可选）" hint="留空时使用 SMTP 账号或收件邮箱"><TextInput type="email" value={inviteMeta.feedbackFromEmail} onChange={event => updateFeedback('feedbackFromEmail', event.target.value)} placeholder="noreply@example.com" maxLength={200} /></Field>
                </div>
                <label className="check-line"><input type="checkbox" checked={inviteMeta.feedbackSmtpSecure} onChange={event => updateFeedback('feedbackSmtpSecure', event.target.checked)} /> 使用 SSL/TLS（465 通常开启；587 通常关闭并使用 STARTTLS）</label>
              </details>
            ) : (
              <div className="feedback-smtp-preset"><span>自动配置</span><strong>{feedbackProviderPresets[feedbackProvider].host}:{feedbackProviderPresets[feedbackProvider].port}</strong><small>{feedbackProviderPresets[feedbackProvider].secure ? 'SSL/TLS 加密' : 'STARTTLS 加密'}</small></div>
            )}
            <div className="inline-actions">
              <Button variant="primary" type="submit" loading={feedbackBusy}><Save size={16} /> 保存邮件反馈设置</Button>
              <Button type="button" onClick={() => void testFeedbackMail()} disabled={feedbackBusy}><Send size={16} /> 发送测试邮件</Button>
            </div>
            <p className="muted small">先保存，再发送测试邮件。若邮箱开启了二次验证，请使用邮箱服务商提供的 SMTP 授权码。发送失败不会丢失玩家反馈，管理员仍可在反馈记录中查看。</p>
          </form>
        )}
      </CollapsiblePanel>

      {permissions.isSuperadmin && (
        <Panel title="AI 留言审核" eyebrow="DEEPSEEK V4 FLASH" className="top-gap">
          {!aiSettings ? <LoadingBlock label="正在加载 AI 审核设置" /> : (
            <div className="ai-settings-layout">
              <form className="stack-form" onSubmit={saveAiSettings}>
                <div className="form-note"><Bot size={17} /><span>AI 审核只处理普通成员的留言和评论；管理员发布内容会直接通过。API Key 仅加密保存在后端。</span></div>
                <div className="ai-model-note"><Badge tone="active">{aiSettings.modelLabel}</Badge><span>固定模型：{aiSettings.model} · 低风险自动公开，中风险进入复核，高风险自动拒绝</span></div>
                <label className="check-line"><input type="checkbox" checked={aiSettings.enabled} onChange={event => updateAi('enabled', event.target.checked)} /> 启用 AI 自动审核</label>
                <Field label="API Key" hint={aiSettings.apiKeyConfigured ? `已配置，末四位 ${aiSettings.apiKeyHint || '****'}；留空不会修改现有密钥` : '只需填写 API Key，接口地址和模型已固定'}>
                  <TextInput type="password" value={aiKey} onChange={event => { setAiKey(event.target.value); setAiClearKey(false); }} placeholder={aiSettings.apiKeyConfigured ? '留空保持现有密钥' : '输入 DeepSeek API Key'} maxLength={400} autoComplete="new-password" disabled={aiClearKey} />
                </Field>
                {aiSettings.apiKeyConfigured && <label className="check-line"><input type="checkbox" checked={aiClearKey} onChange={event => setAiClearKey(event.target.checked)} /> 删除已保存的 API Key</label>}
                <details className="ai-prompt-details">
                  <summary>审核规则与提示词（可选）</summary>
                  <Field label="审核提示词" hint="默认内容已经适合留言板；只有需要调整审核重点时再修改"><Textarea rows={7} maxLength={6000} value={aiSettings.prompt} onChange={event => updateAi('prompt', event.target.value)} required /></Field>
                </details>
                <div className="inline-actions">
                  <Button variant="primary" type="submit" loading={aiBusy}><Save size={16} /> 保存 AI 设置</Button>
                  <Button type="button" onClick={() => void testAiSettings()} disabled={aiBusy}><Bot size={16} /> 测试 API</Button>
                </div>
                {aiTestResult && (
                  <div className={'ai-test-result ' + aiTestResult.decision}>
                    <div><strong>测试结果</strong><Badge tone={aiTestResult.decision === 'allow' ? 'good' : aiTestResult.decision === 'reject' ? 'danger' : 'warn'}>{aiTestResult.decision === 'allow' ? '允许' : aiTestResult.decision === 'reject' ? '拒绝' : '人工复核'}</Badge></div>
                    <p>风险分 {aiTestResult.riskScore} · 耗时 {aiTestResult.latencyMs}ms · 模型 {aiTestResult.model || '—'}</p>
                    <p>{aiTestResult.reason}</p>
                    {aiTestResult.categories.length > 0 && <small>分类：{aiTestResult.categories.join('、')}</small>}
                  </div>
                )}
              </form>

              <section className="ai-review-queue">
                <header><div><span className="eyebrow">REVIEW QUEUE</span><h3>待人工复核 · {aiPending.length}</h3></div><Button type="button" onClick={() => void loadAiModeration()} disabled={aiBusy}>刷新</Button></header>
                {aiPending.length === 0 ? <p className="muted">当前没有待复核内容。</p> : <div className="ai-review-list">{aiPending.map(item => (
                  <article className="ai-review-item" key={item.kind + ':' + item.id}>
                    <header><div><strong>{item.kind === 'message' ? '留言' : '评论'} · {item.displayName}</strong><small>@{item.username} · {formatTime(item.createdAt)}</small></div><Badge tone="warn">风险 {item.moderationScore ?? '—'}</Badge></header>
                    <p>{item.content}</p>
                    <small>{item.moderationReason || '等待人工复核'}{item.moderationCategories.length > 0 ? ' · ' + item.moderationCategories.join('、') : ''}</small>
                    <div className="inline-actions"><Button variant="primary" type="button" onClick={() => void reviewAiItem(item, 'approve')}>通过并公开</Button><Button variant="danger" type="button" onClick={() => void reviewAiItem(item, 'reject')}>拒绝</Button></div>
                  </article>
                ))}</div>}
              </section>
            </div>
          )}
        </Panel>
      )}
    </>
  );
}









